import React from 'react';

const MealCard = ({ meal, isLiked, onLike, compact = false }) => {
  // Determine the season for styling
  const season = meal.season?.toLowerCase() || '';
  const isSpringOrSummer = season.includes('spring') || season.includes('summer');
  const isFallOrWinter = season.includes('fall') || season.includes('winter');
  const isClassic = season.includes('classic');
  const isEssential = season.includes('essential');
  
  // Season-specific styles
  let seasonalStyles = '';
  let seasonalGradient = '';
  let seasonalBorder = '';
  let seasonalLabel = '';
  let seasonTag = '';
  
  if (isSpringOrSummer) {
    seasonalStyles = 'bg-gradient-to-br from-green-50 to-yellow-50';
    seasonalGradient = 'from-green-200 to-yellow-200';
    seasonalBorder = 'border-l-4 border-green-400';
    seasonalLabel = 'bg-green-100 text-green-800';
    seasonTag = 'S/S';
  } else if (isFallOrWinter) {
    seasonalStyles = 'bg-gradient-to-br from-amber-50 to-red-50';
    seasonalGradient = 'from-amber-200 to-red-200';
    seasonalBorder = 'border-l-4 border-amber-400';
    seasonalLabel = 'bg-amber-100 text-amber-800';
    seasonTag = 'F/W';
  } else if (isEssential) {
    seasonalStyles = 'bg-white';
    seasonalBorder = 'border-l-4 border-blue-400';
    seasonalLabel = 'bg-blue-100 text-blue-800';
    seasonTag = 'P';
  } else if (isClassic) {
    // Essential or others
    seasonalStyles = 'bg-white';
    seasonalBorder = 'border-l-4 border-gray-300';
    seasonalLabel = 'bg-gray-100 text-gray-800';
    seasonTag = 'C';
  }
  
  // Conditional classes based on compact mode
  const containerClasses = compact 
    ? `overflow-hidden shadow-sm rounded-lg hover:shadow-md transition-shadow p-2 flex flex-col h-full text-xs ${seasonalBorder} ${seasonalStyles}`
    : `overflow-hidden shadow-sm rounded-lg hover:shadow-md transition-shadow p-3 flex flex-col h-full text-sm ${seasonalBorder} ${seasonalStyles}`;
    
  const headingClasses = compact
    ? "text-sm font-medium flex-1 leading-tight"
    : "text-base font-medium flex-1 leading-tight";
    
  const buttonClasses = compact
    ? `w-full ${isLiked ? 'bg-pink-200 text-pink-800' : isSpringOrSummer ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' : isFallOrWinter ? 'bg-gradient-to-r from-amber-500 to-red-600 text-white' : 'bg-pink-600 text-white'} rounded py-1 text-xs hover:opacity-90 transition-all`
    : `w-full ${isLiked ? 'bg-pink-200 text-pink-800' : isSpringOrSummer ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' : isFallOrWinter ? 'bg-gradient-to-r from-amber-500 to-red-600 text-white' : 'bg-pink-600 text-white'} rounded py-1.5 text-sm hover:opacity-90 transition-all`;
    
  return (
    <div 
      id={meal.id}
      className={containerClasses}
    >
      <div className="flex-grow">
        <div className="flex justify-between items-start gap-1 mb-1">
          <h3 className={headingClasses}>
            {meal.name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
          </h3>
          {!compact && (
            <div className="flex flex-col items-end gap-1 min-w-fit">
              <span className="bg-blue-500 text-white px-1.5 py-0.5 rounded text-xs whitespace-nowrap">
                {meal.calories} cal
              </span>
              <span className="bg-pink-100 text-pink-800 px-1.5 py-0.5 rounded text-xs whitespace-nowrap">
                {meal.Protein?.toLowerCase() === 'vegetarian' ?
                  (meal.Eggs === 'no' && meal.Dairy === 'no' ? 'Vegan' :
                  (meal.Eggs === 'optional' || meal.Dairy === 'optional') ? 'Vegan Opt' :
                  'Veg')
                  : meal.Protein?.charAt(0).toUpperCase() + meal.Protein?.slice(1)}
              </span>
            </div>
          )}
          {compact && (
            <div className="flex flex-col items-end text-[10px]">
              <span className="bg-blue-500 text-white px-1 py-0.5 rounded whitespace-nowrap mb-0.5">
                {meal.calories} cal
              </span>
              <div className="flex space-x-1">
                <span className={`${seasonalLabel} px-1 py-0.5 rounded whitespace-nowrap`}>
                  {seasonTag}
                </span>
                <span className="bg-pink-100 text-pink-800 px-1 py-0.5 rounded whitespace-nowrap">
                  {meal.Protein?.toLowerCase() === 'vegetarian' ? 'Veg' : 
                   meal.Protein?.charAt(0).toUpperCase() + meal.Protein?.slice(1)}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className={`${compact ? 'text-[10px]' : 'text-xs'} text-gray-600 space-y-0.5`}>
          {!compact && (
            <span className={`capitalize block px-1.5 py-0.5 rounded-sm text-center ${seasonalLabel}`}>
              {meal.season}
            </span>
          )}
          {!compact && (
            <>
              <div className="flex items-center justify-between">
                <span>{meal.waitTime ? `${meal.prepTime} + ${meal.waitTime}` : meal.prepTime}</span>
              </div>
              <div className="flex items-center justify-between text-gray-700">
                <span>Sodium: {meal.sodium}mg</span>
                <span>Carbs: {meal.carbs}g</span>
              </div>
            </>
          )}
          {compact && (
            <div className="flex justify-between">
              <span>{meal.waitTime ? `${meal.prepTime} + ${meal.waitTime}` : meal.prepTime}</span>
            </div>
          )}
          {compact && (
            <div className="flex justify-between text-gray-700">
              <span>Sodium: {meal.sodium}mg</span>
              <span>Carbs: {meal.carbs}g</span>
            </div>
          )}
        </div>
      </div>
      <div className={compact ? "mt-1" : "mt-3"}>
        <button
          onClick={() => onLike(meal)}
          className={buttonClasses}
        >
          {isLiked ? 'Remove' : compact ? 'Like' : 'I would like this'}
        </button>
      </div>
    </div>
  );
};

export default MealCard; 